import React, { useCallback, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ENUM_ROLE_USER } from 'constants/enum';
import useUser from 'hooks/useUser';
import Login from 'pages/Login/Login';
import Register from 'pages/Register/Register';
import Report from 'pages/Report/List/Report';
import ViewExample from 'pages/Report/ViewExam/ViewExam';
import ViewSDQ from 'pages/Report/ViewSDQ/ViewSDQ';
import ResetPassword from 'pages/ResetPassword/ResetPassword';
import SampleVideo from 'pages/SampleVideo/List/SampleVideo';
import CreateSurvey from 'pages/Survey/CreateSurvey/CreateSurvey';
import EditSurvey from 'pages/Survey/EditSurvey/EditSurvey';
import Survey from 'pages/Survey/List/Survey';
import SurveyTemplate from 'pages/Survey/SurveyTemplate/SurveyTemplate';
import ViewSurvey from 'pages/Survey/ViewSurvey/ViewSurvey';
import Users from 'pages/Users/List/Users';
import PrivateRoute from 'routes/PrivateRoute';
import PublicRoute from 'routes/PublicRoute';
import AppRoutes from 'routes/routes';

const MainContentLayout = ({ children }) => {
  const { checkUserApprove } = useUser();
  const location = useLocation();

  const getProfile = useCallback(async () => {
    await checkUserApprove();
  }, [location.pathname]);

  useEffect(() => {
    getProfile();
  }, [getProfile]);

  return (
    <main data-testid="main" id="main" className="main-content min-h-body w-full bg-white-714/50 py-main-top min">
      {children}
    </main>
  );
};

const AppRoutesComponent = () => {
  const { isAuthenticated, me } = useSelector((state) => state.auth);

  const public_route = [
    { path: AppRoutes.register, element: <Register /> },
    { path: AppRoutes.resetPassword, element: <ResetPassword /> },
  ];
  const private_route = [
    { path: AppRoutes.survey, element: <Survey /> },
    { path: AppRoutes.surveyCreate, element: <CreateSurvey /> },
    { path: AppRoutes.surveyEdit, element: <EditSurvey /> },
    { path: AppRoutes.surveyView, element: <ViewSurvey /> },
    { path: AppRoutes.report, element: <Report /> },
    { path: AppRoutes.reportView, element: <ViewExample /> },
    { path: AppRoutes.surveyTemplate, element: <SurveyTemplate /> },
    { path: AppRoutes.reportSDQ, element: <ViewSDQ /> },
    { path: AppRoutes.sampleVideo, element: <SampleVideo /> },
  ];
  const adminRoute = [{ path: AppRoutes.users, element: <Users /> }];

  return (
    <Routes>
      {/* Public Route */}
      <Route path={AppRoutes.login} element={<PublicRoute isAuthenticated={isAuthenticated} element={<Login />} role={me?.role} />} />

      {/* Public Route with <MainContentLayout /> */}
      {public_route.map((route, index) => (
        <Route
          key={`public-route-${index}`}
          path={route.path}
          element={<PublicRoute isAuthenticated={isAuthenticated} element={<MainContentLayout>{route.element}</MainContentLayout>} role={me?.role} />}
        />
      ))}

      {/* Private Route */}
      <Route
        path="*"
        element={
          <MainContentLayout>
            <PrivateRoute
              element={
                <Routes>
                  {private_route.map((route, index) => (
                    <Route key={`private-route-${index}`} path={route.path} element={route.element} />
                  ))}
                  {me?.role === ENUM_ROLE_USER.ADMIN &&
                    adminRoute.map((route, index) => <Route key={`admin-route-${index}`} path={route.path} element={route.element} />)}

                  {me?.role === ENUM_ROLE_USER.USER ? (
                    <Route path="*" element={<Navigate to={AppRoutes.survey} replace />} />
                  ) : (
                    <Route path="*" element={<Navigate to={AppRoutes.users} replace />} />
                  )}
                </Routes>
              }
              isAuthenticated={isAuthenticated}
            />
          </MainContentLayout>
        }
      />
    </Routes>
  );
};

export default React.memo(AppRoutesComponent);
